import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader, ShieldCheck, X, AlertCircle } from 'lucide-react'
import {
  DeviceMember,
  HttpError,
  STEP_UP_EVENT,
  deviceApi,
  describeAction,
  getSharedSession,
  handleSharedSessionUnauthorized,
  startParentWindow,
} from '@/utils/sharedDevice'

const POLL_MS = 3000
/** After a dismissal, prompts caused by page loads (GET) stay quiet this long. */
const GET_PROMPT_QUIET_MS = 60_000

type Phase =
  | { kind: 'closed' }
  | { kind: 'choose' }
  | { kind: 'sending' }
  | {
      kind: 'waiting'
      id: string
      code: string
      expiresAt: number
      parent: DeviceMember
      notified: boolean
    }
  | { kind: 'denied'; parent: DeviceMember }
  | { kind: 'expired'; reason: 'timeout' | 'window' }
  | { kind: 'error'; message: string }

const BTN =
  'min-h-[56px] px-6 rounded-xl text-lg font-semibold transition-colors focus:outline-none focus:ring-4 focus:ring-yellow-300'

/**
 * "Ask a parent to approve". Opened when a parent avatar is tapped on the
 * picker (the usual path), or by the API client when a shared-device
 * session gets 403 step_up_required. The device asks the chosen parent,
 * shows the fallback code, polls, and only on approval opens the parent
 * session for its 10-minute window.
 */
export const StepUpSheet: React.FC = () => {
  const navigate = useNavigate()
  const [fromPicker, setFromPicker] = useState(false)
  const [phase, setPhase] = useState<Phase>({ kind: 'closed' })
  const [action, setAction] = useState('Parent action')
  const [parents, setParents] = useState<DeviceMember[]>([])
  const [parentId, setParentId] = useState<string>('')
  const [now, setNow] = useState(() => Date.now())
  const lastDismissed = useRef(0)
  const phaseRef = useRef(phase)
  phaseRef.current = phase
  const firstButton = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  const loadParents = useCallback(async (preferredId?: string) => {
    let list = deviceApi.cachedMembers()
    if (list.length === 0) {
      try {
        list = await deviceApi.members()
      } catch {
        list = []
      }
    }
    const ps = list.filter((m) => m.is_parent)
    setParents(ps)
    const current = getSharedSession()?.member
    const preferred = ps.find((p) => p.id === preferredId)?.id
    setParentId(preferred || (current?.is_parent ? current.id : ps[0]?.id || ''))
  }, [])

  useEffect(() => {
    const onStepUp = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      if (phaseRef.current.kind !== 'closed') return
      if (detail.source === 'picker') {
        setFromPicker(true)
        setAction('Open parent tools')
        setPhase({ kind: 'choose' })
        loadParents(detail.parentId)
        return
      }
      if (detail.method === 'GET' && Date.now() - lastDismissed.current < GET_PROMPT_QUIET_MS) return
      setFromPicker(false)
      setAction(describeAction(detail.method || 'GET', detail.path || ''))
      setPhase({ kind: 'choose' })
      loadParents()
    }
    window.addEventListener(STEP_UP_EVENT, onStepUp)
    return () => window.removeEventListener(STEP_UP_EVENT, onStepUp)
  }, [loadParents])

  useEffect(() => {
    if (phase.kind !== 'closed') {
      const t = setTimeout(() => firstButton.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [phase.kind])

  const close = useCallback(() => {
    lastDismissed.current = Date.now()
    setPhase({ kind: 'closed' })
  }, [])

  useEffect(() => {
    if (phase.kind === 'closed') return
    // TV remotes: arrow keys move between the sheet's buttons.
    // Runs in the capture phase on window and stops arrow keys and Escape
    // there, so the picker's remote navigation behind the sheet never moves
    // focus out of it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        close()
        return
      }
      const dir = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key]
      if (!dir || !dialogRef.current) return
      e.stopPropagation()
      e.preventDefault()
      const buttons = Array.from(dialogRef.current.querySelectorAll<HTMLButtonElement>('button:not([disabled])'))
      if (buttons.length === 0) return
      const i = buttons.indexOf(document.activeElement as HTMLButtonElement)
      buttons[(i + dir + buttons.length) % buttons.length].focus()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [phase.kind, close])

  const send = async () => {
    const parent = parents.find((p) => p.id === parentId)
    if (!parent) return
    setPhase({ kind: 'sending' })
    try {
      const r = await deviceApi.stepUp(parent.id, action)
      const expiresAt = Date.parse(r.expires_at) || Date.now() + (r.expires_in || 300) * 1000
      const notified = typeof r.notified === 'number' ? r.notified > 0 : !!r.notified
      setPhase({ kind: 'waiting', id: r.id, code: r.fallback_code, expiresAt, parent, notified })
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) {
        setPhase({ kind: 'closed' })
        handleSharedSessionUnauthorized()
        return
      }
      setPhase({ kind: 'error', message: 'Could not send the request. Check the connection and try again.' })
    }
  }

  // Poll the request while waiting.
  const waitingId = phase.kind === 'waiting' ? phase.id : null
  useEffect(() => {
    if (!waitingId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const tick = async () => {
      if (cancelled) return
      try {
        const r: any = await deviceApi.stepUpStatus(waitingId)
        if (cancelled) return
        if (r.status === 'approved' && r.token) {
          const u = r.user || {}
          await startParentWindow(r, {
            id: u.id,
            display_name: u.display_name,
            avatar_url: u.avatar_url,
            is_parent: true,
          })
          // startParentWindow cleared the query cache (new session).
          setPhase({ kind: 'closed' })
          if (window.location.pathname === '/device') navigate('/dashboard', { replace: true })
          return
        }
        if (r.status === 'approved') {
          setPhase({ kind: 'expired', reason: 'window' })
          return
        }
        if (r.status === 'denied') {
          const p = phaseRef.current
          setPhase({ kind: 'denied', parent: p.kind === 'waiting' ? p.parent : { id: '', display_name: 'The parent', is_parent: true } })
          return
        }
        if (r.status === 'expired') {
          setPhase({ kind: 'expired', reason: 'timeout' })
          return
        }
      } catch (err) {
        if (cancelled) return
        if (err instanceof HttpError && err.status === 401) {
          setPhase({ kind: 'closed' })
          handleSharedSessionUnauthorized()
          return
        }
        if (err instanceof HttpError && err.status === 404) {
          setPhase({ kind: 'expired', reason: 'timeout' })
          return
        }
      }
      timer = setTimeout(tick, POLL_MS)
    }
    timer = setTimeout(tick, POLL_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [waitingId, navigate])

  useEffect(() => {
    if (phase.kind !== 'waiting') return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [phase.kind])

  if (phase.kind === 'closed') return null

  const host = window.location.host
  const left = phase.kind === 'waiting' ? Math.max(0, Math.round((phase.expiresAt - now) / 1000)) : 0

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 p-4" role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="step-up-title"
        className="w-full max-w-2xl bg-white text-gray-900 rounded-3xl shadow-2xl p-6 sm:p-8 relative"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 w-14 h-14 rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-yellow-300"
        >
          <X className="w-7 h-7" />
        </button>
        <h2 id="step-up-title" className="text-3xl font-bold mb-2 pr-14 flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-indigo-600" aria-hidden="true" />
          Ask a parent to approve
        </h2>

        {phase.kind === 'choose' && (
          <>
            <p className="text-lg text-gray-600 mb-6">
              {fromPicker
                ? "Parent tools on this screen need approval from a parent's phone. Approval lasts 10 minutes."
                : `${action} needs a parent's approval on this shared screen. Approval lasts 10 minutes.`}
            </p>
            {parents.length === 0 ? (
              <p className="text-lg text-gray-700 mb-6">Loading parents...</p>
            ) : (
              <fieldset className="mb-6">
                <legend className="text-lg font-semibold mb-3">Which parent?</legend>
                <div className="grid sm:grid-cols-2 gap-3">
                  {parents.map((p, i) => (
                    <button
                      key={p.id}
                      ref={i === 0 ? firstButton : undefined}
                      type="button"
                      aria-pressed={parentId === p.id}
                      onClick={() => setParentId(p.id)}
                      className={`${BTN} text-left border-2 ${
                        parentId === p.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {p.display_name}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={send}
                disabled={!parentId}
                className={`${BTN} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
              >
                Ask for approval
              </button>
              <button type="button" onClick={close} className={`${BTN} bg-gray-100 hover:bg-gray-200`}>
                Not now
              </button>
            </div>
          </>
        )}

        {phase.kind === 'sending' && (
          <p className="text-lg flex items-center gap-3 py-6" role="status">
            <Loader className="w-6 h-6 animate-spin" /> Sending...
          </p>
        )}

        {phase.kind === 'waiting' && (
          <div role="status">
            <p className="text-xl font-semibold mt-2 mb-1">
              {phase.notified
                ? `Approval sent to ${phase.parent.display_name}'s phone.`
                : `${phase.parent.display_name} has no phone set up for HousePoints notifications.`}
            </p>
            <p className="text-lg text-gray-600 mb-5">
              {phase.notified ? 'If the notification does not arrive, use' : 'Use'} this code instead: on
              their phone, sign in at <strong>{host}/step-up</strong> and enter
            </p>
            <p className="font-mono font-bold tracking-[0.15em] text-6xl text-center bg-gray-100 rounded-2xl py-5 mb-5">
              {phase.code}
            </p>
            <p className="text-base text-gray-600 flex items-center gap-2 mb-6">
              <Loader className="w-5 h-5 animate-spin" aria-hidden="true" />
              Waiting for approval. The request expires in {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}.
            </p>
            <button type="button" ref={firstButton} onClick={close} className={`${BTN} bg-gray-100 hover:bg-gray-200`}>
              Cancel
            </button>
          </div>
        )}

        {(phase.kind === 'denied' || phase.kind === 'expired' || phase.kind === 'error') && (
          <>
            <p className="text-lg text-gray-700 my-6 flex items-start gap-3" role="alert">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              {phase.kind === 'denied' && `${phase.parent.display_name} declined the request.`}
              {phase.kind === 'expired' &&
                (phase.reason === 'window'
                  ? 'The approval was given, but its 10 minutes have already ended.'
                  : 'The request timed out before a parent approved it.')}
              {phase.kind === 'error' && phase.message}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                ref={firstButton}
                onClick={() => setPhase({ kind: 'choose' })}
                className={`${BTN} bg-indigo-600 text-white hover:bg-indigo-700`}
              >
                Ask again
              </button>
              <button type="button" onClick={close} className={`${BTN} bg-gray-100 hover:bg-gray-200`}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default StepUpSheet
